import React, { useState } from 'react';
import { BarChart3, EyeIcon } from 'lucide-react';
import { Button } from './ui/button';
import { JointDataModal } from './JointDataModal';
import { Device } from '../features/device/types/device';

interface ProcessDataProps {
    apiResponse?: any;
    edgeUid?: string;
    device?: Device;
}

export const ProcessData: React.FC<ProcessDataProps> = ({
    apiResponse,
    edgeUid,
    device,
}) => {
    const [isJointDataModalOpen, setIsJointDataModalOpen] = useState(false);
    const jointCloseData = apiResponse?.jointCloseData;

    const displayData = {
        jointVolume: {
            actual: jointCloseData?.jointVolume ?? jointCloseData?.JointVolume ?? 0,
            unit: 'Litres',
        },
        chargeVolume: {
            actual: jointCloseData?.chargedVolume ?? 0,
            unit: 'Litres',
        },
        fillTime: {
            actual: jointCloseData?.fill_time ?? jointCloseData?.['Fill Time'] ?? 0,
            unit: 'Seconds',
        },
        totalTime: {
            actual: jointCloseData?.totalTime ?? 0,
            unit: 'Seconds',
        },
    };

    return (
        <>
            <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-blue-500" />
                        Process Data
                    </h3>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!edgeUid}
                        onClick={() => setIsJointDataModalOpen(true)}
                    >
                       <EyeIcon className="h-4 w-4" /> Joint Data
                    </Button>
                </div>
                <div className="p-6">
                    {!jointCloseData ? (
                        <div className="text-center py-12 text-gray-500">
                            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-lg font-medium">No process data available</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center p-4 bg-blue-50 rounded-lg border">
                                <div className="text-lg font-bold text-blue-700">
                                    {displayData.jointVolume.actual} {displayData.jointVolume.unit}
                                </div>
                                <div className="text-xs text-blue-600 mt-1">Joint Volume</div>
                            </div>
                            <div className="text-center p-4 bg-green-50 rounded-lg border">
                                <div className="text-lg font-bold text-green-700">
                                    {displayData.chargeVolume.actual} {displayData.chargeVolume.unit}
                                </div>
                                <div className="text-xs text-green-600 mt-1">Charge Volume</div>
                            </div>
                            <div className="text-center p-4 bg-purple-50 rounded-lg border">
                                <div className="text-lg font-bold text-purple-700">
                                    {displayData.fillTime.actual} {displayData.fillTime.unit}
                                </div>
                                <div className="text-xs text-purple-600 mt-1">Fill Time</div>
                            </div>
                            <div className="text-center p-4 bg-orange-50 rounded-lg border">
                                <div className="text-lg font-bold text-orange-700">
                                    {displayData.totalTime.actual} {displayData.totalTime.unit}
                                </div>
                                <div className="text-xs text-orange-600 mt-1">Total Time</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <JointDataModal
                isOpen={isJointDataModalOpen}
                onClose={() => setIsJointDataModalOpen(false)}
                edgeUid={edgeUid}
                device={device}
            />
        </>
    );
};
